import React, { useCallback, useContext, useEffect, useState } from 'react';

import { navigate } from '@reach/router';

import { GlobalContext } from '../../../../store';

import { InnerRouter } from '../../../../components/AnimatedRouter';
import Stepper from '../../../../components/Stepper';
import { IStep } from '../../../../components/Stepper/Stepper';
import { PlayerEditorModal } from '../PlayerEditorModal/PlayerEditorModal';

interface IProps {
  clubId: string;
  children: any;
  roundId: string;
  eventId: string;
}

export const RoundSetup = (props: IProps) => {
  const { children, clubId, eventId, roundId } = props;

  const {
    actions,
    adminEventRounds,
    adminEvents: { current: event },
    adminEventRounds: { current: round },

    adminRoundFormats,
    actions: {
      adminEventRounds: { setCurrent: setCurrentRound }
    }
  } = useContext(GlobalContext);

  useEffect(() => {
    if (adminEventRounds && adminEventRounds[Number(roundId)]) {
      setCurrentRound(adminEventRounds[Number(roundId)]);
    }
  }, [adminEventRounds[Number(roundId)], roundId]);

  const locationGroups = location
    ? /staff\/club\/[0-9]+\/event\/[0-9]+\/round\/[0-9]+\/([a-z\-]+)/g.exec(location.pathname || '')
    : [];

  const currentPage = locationGroups && locationGroups.length > 1 ? locationGroups[1] : 'setup';

  useEffect(() => {
    if (roundId && eventId) {
      actions.adminEventRounds.get(Number(clubId), Number(eventId), Number(roundId));
      actions.adminRoundFormats.get(Number(clubId), Number(eventId), Number(roundId));
    }
  }, [roundId]);

  const roundSetupDone =
    round && round.playersPerTee && round.start && (round.shotgunStart || round.teeFrequency);

  const teeSheetDone =
    round &&
    (round.groups || []).reduce((acc, group) => acc + group.games.length, 0) ===
      (event ? event.totalPlayers : 0);

  interface IExtendedStep extends IStep {
    slug: string;
    content?: JSX.Element;
  }

  const [contestsSelected, setContestsSelected] = useState(false);

  useEffect(() => {
    if (adminRoundFormats[Number(roundId)]) {
      setContestsSelected(
        (adminRoundFormats[Number(roundId)] || []).filter(
          roundFormat => roundFormat.format.isContest
        ).length > 0
      );
    }
  }, [adminRoundFormats, roundId]);

  const steps: IExtendedStep[] =
    !event || !round
      ? [{ content: <div />, slug: '', name: '', status: 'done' }]
      : ([
          {
            name: 'Setup',
            slug: 'setup',
            status: roundSetupDone ? 'done' : ''
          },
          round.order === 0 && event.allowTeams
            ? {
                name: 'Teams',
                slug: 'teams',
                status: ''
              }
            : null,
          {
            name: 'Tee Sheet',
            slug: 'teesheet',
            status: teeSheetDone ? 'done' : ''
          },
          contestsSelected
            ? {
                name: 'Contests',
                slug: 'contest',
                status: ''
              }
            : null,
          round.order === 0 && event.useFlights
            ? {
                name: 'Flights',
                slug: 'flights',
                status: roundSetupDone ? 'done' : ''
              }
            : null,
          {
            name: 'Launch',
            slug: 'launch',
            status:
              round && (round.status === 'published' || round.status === 'completed') ? 'done' : ''
          },
          {
            name: 'Results',
            slug: 'results',
            status: 'done'
          },
          // Disabled due to https://code.talpor.com/club15/frontend/-/issues/888
          // {
          //   name: 'Awards',
          //   slug: 'awards',
          //   status: ''
          // },
          {
            name: 'Complete',
            slug: 'complete',
            status: round && round.status === 'completed' ? 'done' : ''
          }
        ].filter(Boolean) as IExtendedStep[]);

  const setStep = (_: any, index: number) =>
    navigate(`/staff/club/${clubId}/event/${eventId}/round/${roundId}/${steps[index].slug}`);

  const activeStep: number =
    !event || !round ? 0 : steps.findIndex(step => step.slug === currentPage);

  const getRouterDirection = useCallback(
    (oldPath: string, newPath: string) => {
      const oldGroups = /staff\/club\/[0-9]+\/event\/[0-9]+\/round\/[0-9]+\/([a-z\-]+)/g.exec(
        oldPath
      );
      const oldPage = oldGroups && oldGroups.length > 1 ? oldGroups[1] : 'setup';
      const newGroups = /staff\/club\/[0-9]+\/event\/[0-9]+\/round\/[0-9]+\/([a-z\-]+)/g.exec(
        newPath
      );
      const newPage = newGroups && newGroups.length > 1 ? newGroups[1] : 'setup';

      const newPageIndex = steps.findIndex(step => step.slug === newPage);
      const oldPageIndex = steps.findIndex(step => step.slug === oldPage);

      return newPageIndex < oldPageIndex ? 'reverse' : '';
    },
    [steps]
  );

  return round && roundId ? (
    <>
      <Stepper steps={steps} active={activeStep} onChange={setStep} />

      <PlayerEditorModal />

      <div className="admin-wrapper">
        {children && (
          <InnerRouter
            location={location}
            className="event-setup-router"
            direction={getRouterDirection}
          >
            {children || <div />}
          </InnerRouter>
        )}
      </div>
    </>
  ) : (
    <></>
  );
};
